class HeroSlider extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-slider__slide'));
    this.dots = Array.from(this.querySelectorAll('.hero-slider__dot'));
    this.toggleButton = this.querySelector('.hero-slider__toggle');
    this.index = 0;
    this.interval = Math.max(Number(this.dataset.interval) || 6, 1) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
    this.userPaused = false;
    this.hoverPaused = false;

    if (this.slides.length < 2) return;

    this.querySelector('.hero-slider__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('.hero-slider__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.toggleButton?.addEventListener('click', () => {
      this.userPaused = !this.userPaused;
      this.updateTimer();
    });

    if (this.dataset.pauseOnHover === 'true') {
      this.addEventListener('mouseenter', () => { this.hoverPaused = true; this.updateTimer(); });
      this.addEventListener('mouseleave', () => { this.hoverPaused = false; this.updateTimer(); });
    }
    this.addEventListener('focusin', () => { this.hoverPaused = true; this.updateTimer(); });
    this.addEventListener('focusout', () => { this.hoverPaused = false; this.updateTimer(); });

    this.addEventListener('touchstart', (e) => { this.touchX = e.touches[0].clientX; }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (this.touchX == null) return;
      const delta = e.changedTouches[0].clientX - this.touchX;
      this.touchX = null;
      if (Math.abs(delta) > 40) this.go(this.index + (delta < 0 ? 1 : -1));
    });

    this.onVisibilityChange = () => this.updateTimer();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // Theme editor: jump to the slide being edited and hold it there.
    this.onBlockSelect = (e) => {
      const i = this.slides.findIndex((slide) => slide.dataset.blockId === e.detail.blockId);
      if (i === -1) return;
      this.editorPaused = true;
      this.go(i);
    };
    this.onBlockDeselect = () => { this.editorPaused = false; this.updateTimer(); };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.go(0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  go(target) {
    const count = this.slides.length;
    const next = ((target % count) + count) % count;
    const previous = this.index;
    this.index = next;

    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-leaving', i === previous && !active);
      slide.setAttribute('aria-hidden', String(!active));
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });
    // Start loading the current and upcoming slide so neither appears blank.
    [next, (next + 1) % count].forEach((i) => {
      this.slides[i].querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === next)));

    // Restart the countdown so every slide, including one picked by hand, gets its full time.
    this.updateTimer();
  }

  get isPaused() {
    return !this.autoplay || this.userPaused || this.hoverPaused || this.editorPaused || document.hidden;
  }

  updateTimer() {
    clearTimeout(this.timer);
    this.timer = null;
    this.toggleButton?.setAttribute('aria-pressed', String(this.userPaused));
    this.classList.toggle('is-paused', this.userPaused);
    if (!this.isPaused) this.schedule();
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      if (!this.isPaused) this.go(this.index + 1);
    }, this.interval);
  }
}

if (!customElements.get('hero-slider')) customElements.define('hero-slider', HeroSlider);
